import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTenantVertical } from "@/hooks/useTenantVertical";
import { ArrowRight, UserCog, Inbox, CalendarCheck } from "lucide-react";

import { usePageViewTracking, useConversionTracking } from "@/hooks/useConversionTracking";
import { useAnalyticsTracking, useCtaTracking } from "@/hooks/useAnalyticsTracking";
import { STRIPE_PLANS } from "@/lib/stripe-config";

import { Button } from "@/components/ui/button";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { PublicShell } from "@/components/landing/PublicShell";
import { HeroAurora } from "@/components/landing/HeroAurora";
import { HeroTrialPanel } from "@/components/landing/HeroTrialPanel";

import { type TrainingTrialHandle } from "@/components/landing/TrainingTrial";
import { AuthDialog } from "@/components/landing/AuthDialog";
import { FAQSection } from "@/components/landing/FAQSection";
import { ROISection } from "@/components/landing/ROISection";
import { HonestTransparency } from "@/components/landing/HonestTransparency";
import { RealProof } from "@/components/landing/RealProof";
import { VerticalBadge } from "@/components/landing/VerticalBadge";
import { TrustCompressed } from "@/components/landing/TrustCompressed";
import { ScrollProgress } from "@/components/landing/ScrollProgress";
import { ServiceFlow } from "@/components/landing/ServiceFlow";
import { DeliverablesGrid } from "@/components/landing/DeliverablesGrid";
import { PortalShowcase } from "@/components/landing/PortalShowcase";
import { ServicePricingCard } from "@/components/landing/ServicePricingCard";
import { MagneticButton } from "@/components/motion/MagneticButton";
import { Reveal } from "@/components/motion/Reveal";
import { LiveDivider } from "@/components/motion/LiveDivider";
import { CinematicStage } from "@/components/cinematic/CinematicStage";
import { Scene } from "@/components/cinematic/Scene";

export default function Auth() {
  usePageViewTracking("landing");
  useConversionTracking();
  useAnalyticsTracking();
  useCtaTracking();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pendingPlan = searchParams.get("plan") as keyof typeof STRIPE_PLANS | null;
  const verticalParam = searchParams.get("v");
  const verticalSlug = useTenantVertical(verticalParam);

  const [authOpen, setAuthOpen] = useState(false);
  const [authMode, setAuthMode] = useState<"signin" | "signup">("signup");
  const [trialKind, setTrialKind] = useState<"idle" | "loading" | "result" | "error">("idle");
  const trialExpanded = trialKind === "loading" || trialKind === "result";
  const howItWorksRef = useRef<HTMLDivElement>(null);
  const trialSectionRef = useRef<HTMLDivElement>(null);
  const trialHandleRef = useRef<TrainingTrialHandle>(null);

  // Force dark theme on this page; restore on unmount.
  useEffect(() => {
    const root = document.documentElement;
    const wasLight = root.classList.contains("light");
    const wasDark = root.classList.contains("dark");
    root.classList.remove("light");
    root.classList.add("dark");
    return () => {
      root.classList.remove("dark");
      if (wasLight) root.classList.add("light");
      if (wasDark) root.classList.add("dark");
    };
  }, []);

  // Open dialog automatically if landed with ?plan=
  useEffect(() => {
    if (pendingPlan && STRIPE_PLANS[pendingPlan]) {
      setAuthMode("signup");
      setAuthOpen(true);
    }
  }, [pendingPlan]);

  // Capture trial-email CTA: stash trial_lead_id, fire analytics, auto-open signup.
  useEffect(() => {
    const tlid = searchParams.get("trial_lead_id");
    const intent = searchParams.get("intent");
    if (tlid) {
      try {
        localStorage.setItem("trial_lead_id", tlid);
      } catch {
        /* ignore */
      }
      void import("@/hooks/useAnalyticsTracking").then((m) =>
        m.trackEvent("trial_email_cta_clicked", {
          trial_lead_id: tlid,
          utm_source: searchParams.get("utm_source"),
          utm_campaign: searchParams.get("utm_campaign"),
        }),
      );
    }
    if (intent === "signup") {
      setAuthMode("signup");
      setAuthOpen(true);
    }
  }, [searchParams]);

  const openSignup = () => {
    setAuthMode("signup");
    setAuthOpen(true);
  };
  const openSignin = () => {
    setAuthMode("signin");
    setAuthOpen(true);
  };
  const scrollToHow = () => howItWorksRef.current?.scrollIntoView({ behavior: "smooth" });
  const scrollToTrial = (source: "hero" | "demo" | "scroll" = "scroll") => {
    trialSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // Focus the input after the smooth-scroll settles
    window.setTimeout(() => trialHandleRef.current?.focusInput(source), 700);
  };
  const handleHeroTryClick = () => {
    void import("@/hooks/useAnalyticsTracking").then((m) =>
      m.trackEvent("hero_try_on_site_clicked", { target: "trial" }),
    );
    scrollToTrial("hero");
  };

  return (
    <PublicShell>
      <ScrollProgress />
      <CinematicStage>
      <div className="overflow-y-auto">
        <LandingNav onSignIn={openSignin} showHowItWorks={scrollToHow} showLanguageSelector />

        <main id="main">
        {/* ─── 1. HERO — Outcome + Trial ─── */}
        <Scene reveal="zoom"><HeroAurora intensity={0.25}>
          <div className="max-w-4xl mx-auto px-4 pt-12 pb-16 md:pt-16 md:pb-20 transition-all duration-500">
            <div className="flex flex-col gap-8 md:gap-10 items-center text-center">
              <div className="space-y-5 max-w-2xl mx-auto text-center">
                <Reveal stagger={60} className="space-y-5">
                  {verticalSlug && (
                    <div className="flex justify-center">
                      <VerticalBadge slug={verticalSlug} />
                    </div>
                  )}
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-border bg-card/60 backdrop-blur-xl text-[10px] font-mono uppercase tracking-widest text-foreground/75 mx-auto">
                    <span
                      aria-hidden
                      className="h-1.5 w-1.5 rounded-full bg-success"
                      style={{
                        boxShadow: "0 0 6px hsl(var(--success) / 0.7)",
                        animation: "breathe-soft 2.4s ease-in-out infinite",
                      }}
                    />
                    Live · managed outbound
                  </div>
                  <h1
                    className={
                      trialExpanded
                        ? "text-2xl md:text-3xl font-bold text-foreground tracking-tight leading-[1.1] drop-shadow-[0_2px_24px_hsl(var(--primary)/0.3)]"
                        : "text-3xl md:text-5xl font-bold text-foreground tracking-tight leading-[1.05] drop-shadow-[0_2px_24px_hsl(var(--primary)/0.35)]"
                    }
                  >
                    {trialExpanded
                      ? "This is what we'd send on your behalf."
                      : "Booked meetings. Delivered as a service."}
                  </h1>
                  <p className="text-base md:text-lg text-foreground/90 leading-relaxed max-w-xl mx-auto">
                    {trialExpanded
                      ? "Built live from your URL. This is the exact quality we'd run in your inboxes if you were a client today."
                      : "Our team runs the outbound machine — inboxes, prospecting, writing, replies, deliverability. You get qualified meetings on your calendar and a portal to watch it happen."}
                  </p>
                </Reveal>

                {!trialExpanded && (
                  <Reveal stagger={60} delay={300} className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 pt-1">
                    {[
                      { icon: UserCog, text: "Dedicated account manager", color: "hsl(var(--success))" },
                      { icon: Inbox, text: "Warmed inbox infrastructure", color: "hsl(var(--primary))" },
                      { icon: CalendarCheck, text: "First qualified meeting is free", color: "hsl(var(--primary-glow))" },
                    ].map((b, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-foreground/75">
                        <b.icon className="h-3.5 w-3.5" style={{ color: b.color }} />
                        <span>{b.text}</span>
                      </div>
                    ))}
                  </Reveal>
                )}
              </div>

              <Reveal delay={200} className="w-full">
                <div ref={trialSectionRef} className="w-full max-w-3xl lg:max-w-4xl mx-auto">
                  <HeroTrialPanel
                    ref={trialHandleRef}
                    onSignup={openSignup}
                    expanded={trialExpanded}
                    onStateChange={setTrialKind}
                  />
                  {!trialExpanded && (
                    <p className="text-center text-[11px] text-foreground/60 mt-3">
                      See what we'd send from your URL · ~8 seconds · no signup
                    </p>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </HeroAurora></Scene>

        {/* ─── 2. HOW THE SERVICE WORKS ─── */}
        <section ref={howItWorksRef}>
          <Scene reveal="rise"><ServiceFlow /></Scene>
        </section>

        {/* ─── 3. WHAT'S INCLUDED ─── */}
        <Scene reveal="rise"><DeliverablesGrid /></Scene>

        {/* ─── 4. CLIENT PORTAL PREVIEW ─── */}
        <Scene reveal="rise"><PortalShowcase /></Scene>

        {/* ─── 5. REAL PROOF — DB-backed case studies, honest empty state ─── */}
        <RealProof vertical={verticalSlug} />

        {/* ─── 6. HONEST TRANSPARENCY — what we do / don't ─── */}
        <HonestTransparency />

        {/* ─── 7. PRICING — retainer + per meeting ─── */}
        <Scene reveal="rise">
          <ServicePricingCard
            copy={undefined}
            onPrimary={() => scrollToTrial("scroll")}
          />
        </Scene>

        {/* ─── 8. MID-FUNNEL CTA — see the sample ─── */}
        <section className="relative">
          <div className="max-w-3xl mx-auto px-4 py-16 md:py-20 text-center space-y-5">
            <p className="text-[10px] font-mono uppercase tracking-widest text-primary">
              See it on your site
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground tracking-tight">
              Watch what we'd write for <span className="text-gradient-brand">your</span> URL.
            </h2>
            <p className="text-sm text-foreground/85">~8 seconds · no signup required.</p>
            <div className="flex justify-center">
              <MagneticButton strength={4}>
                <Button size="lg" className="text-sm px-7 gap-2 glow-primary" onClick={() => scrollToTrial("scroll")}>
                  Try it on my URL <ArrowRight className="h-4 w-4" />
                </Button>
              </MagneticButton>
            </div>
          </div>
        </section>

        {/* ─── 7. TRUST (compressed) ─── */}
        <TrustCompressed />

        {/* ─── 9. ROI ─── */}
        <Scene reveal="rise">
          <section className="relative">
            <div className="max-w-6xl mx-auto px-4 py-20 md:py-28">
              <ROISection />
            </div>
          </section>
        </Scene>

        {/* ─── 10. FAQ + Objections (single accordion) ─── */}
        <section className="relative">
          <div className="max-w-4xl mx-auto px-4 py-20 md:py-28">
            <FAQSection />
          </div>
        </section>

        {/* ─── 11. FINAL CTA ─── */}
        <section className="relative">
          <div className="max-w-3xl mx-auto px-4 py-20 md:py-28 text-center space-y-6">
            <LiveDivider className="max-w-xs mx-auto" />
            <Reveal stagger={60} className="space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold text-foreground tracking-tight drop-shadow-[0_2px_24px_hsl(var(--primary)/0.4)]">
                Let&apos;s build your pipeline.
              </h2>
              <p className="text-sm md:text-base text-foreground/80 max-w-md mx-auto">
                30-minute strategy call with your future account manager. First qualified meeting is free.
              </p>
            </Reveal>
            <Reveal delay={250}>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <MagneticButton strength={5}>
                  <Button
                    size="lg"
                    className="text-sm px-8 gap-2 glow-primary h-12"
                    data-cta="strategy-call"
                    onClick={openSignup}
                  >
                    Book a strategy call <ArrowRight className="h-4 w-4" />
                  </Button>
                </MagneticButton>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-sm px-6 h-12"
                  onClick={() => scrollToTrial("scroll")}
                >
                  See a live sample
                </Button>
              </div>
            </Reveal>
          </div>
        </section>

        </main>

        <LandingFooter showLogo />
      </div>
      </CinematicStage>

      <AuthDialog
        open={authOpen}
        onOpenChange={setAuthOpen}
        defaultMode={authMode}
        pendingPlan={pendingPlan}
      />
    </PublicShell>
  );
}
